import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenantSession } from "@/contexts/TenantSessionContext";
import { apiResetPassword } from "@/lib/api";

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantSlug } = useTenantSession();

  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // No token → invalid link state
  const noToken = !token;

  // Auto-redirect 2s after success
  useEffect(() => {
    if (!success) return;
    const id = setTimeout(() => navigate("/login"), 2000);
    return () => clearTimeout(id);
  }, [success, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t("resetPassword.tooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.mismatch"));
      return;
    }

    setIsLoading(true);
    try {
      await apiResetPassword(token, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || t("resetPassword.invalidToken"));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/40 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-2xl shadow-elevated p-8 sm:p-10">
          <div className="text-center mb-6">
            <img src="/besthr.png" alt="BestHR" className="h-10 mx-auto mb-4" />
            {tenantSlug && tenantSlug !== "public" && (
              <span className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 font-mono font-medium">
                {tenantSlug}
              </span>
            )}
          </div>

          {success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-foreground">
                {t("resetPassword.successTitle")}
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("resetPassword.successMessage")}
              </p>
              <Button
                className="w-full h-11 gradient-brand text-primary-foreground font-medium mt-4"
                onClick={() => navigate("/login")}
              >
                {t("login.signInButton")}
              </Button>
            </div>
          ) : noToken ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <h1 className="text-xl font-bold text-foreground">
                {t("resetPassword.invalidToken")}
              </h1>
              <Button
                variant="outline"
                className="w-full h-11"
                onClick={() => navigate("/forgot-password")}
              >
                {t("resetPassword.requestNew")}
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground text-center">
                  {t("resetPassword.title")}
                </h1>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium">
                    {t("resetPassword.newPasswordLabel")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                      required
                      autoComplete="new-password"
                      autoFocus
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    {t("resetPassword.confirmLabel")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                      required
                      autoComplete="new-password"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 gradient-brand text-primary-foreground font-medium"
                  disabled={isLoading || !newPassword || !confirmPassword}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("resetPassword.updating", "Updating…")}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      {t("resetPassword.submitButton")}
                    </span>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8">
          {t("login.copyright")}
        </p>
      </div>
    </div>
  );
}
